import React from 'react';
import { useNavigate } from 'react-router-dom';
import subjectPreview from '../assets/Subjectpage.png';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  Video,
  FileText,
  Users,
  Star,
  Briefcase,
  Building,
  TrendingUp,
  Search,
  Bell,
  LineChart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const studyHubProps = [
  { icon: <BookOpen className="h-4 w-4" />, text: "Full IPU Syllabus" },
  { icon: <FileText className="h-4 w-4" />, text: "Verified Notes & PYQs" },
  { icon: <Video className="h-4 w-4" />, text: "Expert Video Lectures" },
];

const ipuCheckerProps = [
  { icon: <LineChart className="h-4 w-4" />, text: "Real-time Results" },
  { icon: <TrendingUp className="h-4 w-4" />, text: "Performance Analytics" },
  { icon: <Bell className="h-4 w-4" />, text: "Instant Notifications" },
];

const jobPortalProps = [
  { icon: <Briefcase className="h-4 w-4" />, text: "Top Internships" },
  { icon: <Building className="h-4 w-4" />, text: "Direct Hiring" },
  { icon: <Search className="h-4 w-4" />, text: "Skill Matching" },
];

const HeroSection = ({ stats, mode = 'studyhub', onJoinClick }) => {
  const navigate = useNavigate();

  const content = {
    studyhub: {
      props: studyHubProps,
      heading: <>#1 Learning Hub <br /><span className="text-foreground">for Students.</span></>,
      subtitle: "Comprehensive resources for IPU B.Tech. Master your Semester with Notes, PYQs, and expert mentorship designed for engineering excellence.",
      cta: "Get Started",
      link: "/signup",
      stats: [
        { label: 'Students', value: stats.students, icon: <Users className="h-4 w-4" /> },
        { label: 'Resources', value: stats.materials, icon: <BookOpen className="h-4 w-4" /> },
        { label: 'Avg Rating', value: `${stats.rating}★`, icon: <Star className="h-4 w-4" /> },
      ]
    },
    ipuchecker: {
      props: ipuCheckerProps,
      heading: <>Check Results <br /><span className="text-foreground">Instantly.</span></>,
      subtitle: "Fastest way to check your GGSIPU results. Get detailed performance reports, SGPA/CGPA calculations, and semester-wise trends.",
      cta: "Check Now",
      link: "/ipu-result",
      stats: [
        { label: 'Checks', value: '50K+', icon: <LineChart className="h-4 w-4" /> },
        { label: 'Accuracy', value: '100%', icon: <TrendingUp className="h-4 w-4" /> },
        { label: 'Users', value: '15K+', icon: <Users className="h-4 w-4" /> },
      ]
    },
    jobportal: {
      props: jobPortalProps,
      heading: <>Build Your Career <br /><span className="text-foreground">Hire Top Talent.</span></>,
      subtitle: "The ultimate bridge between students and recruiters. Find the best internships, jobs, and candidate matches with our deep skill analysis.",
      cta: "Join the Portal",
      link: "/job-portal",
      stats: [
        { label: 'Active Jobs', value: '250+', icon: <Briefcase className="h-4 w-4" /> },
        { label: 'Companies', value: '50+', icon: <Building className="h-4 w-4" /> },
        { label: 'Placements', value: '1.2K+', icon: <Star className="h-4 w-4" /> },
      ]
    }
  };

  const current = content[mode] || content.studyhub;

  const handleAction = () => {
    if (onJoinClick) {
      onJoinClick(current.link);
    } else {
      // Ensure we navigate to the correct link based on current mode
      navigate(current.link);
    }
  };

  return (
    <section className="relative pt-32 md:pt-40 px-6 pb-20 overflow-hidden min-h-screen bg-background flex flex-col items-center text-center transition-colors duration-300">
      {/* Dot Grid Background */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none bg-[radial-gradient(circle,_var(--border)_1px,_transparent_1px)] bg-[length:32px_32px]" />

      {/* Decorative Glows */}
      <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[70vw] h-[50vh] bg-primary/10 blur-[120px] rounded-full z-0 pointer-events-none" />

      <motion.div
        key={mode}
        className="relative z-10 max-w-5xl w-full"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Value Props Badge */}
        <div className="flex justify-center gap-3 md:gap-6 mb-12 flex-wrap">
          {current.props.map((prop, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 * i + 0.3 }}
            >
              <Badge variant="outline" className="px-4 py-2 bg-card/50 backdrop-blur-sm border-border text-xs md:text-sm font-medium flex items-center gap-2 rounded-full">
                <span className="text-primary">{prop.icon}</span>
                {prop.text}
              </Badge>
            </motion.div>
          ))}
        </div>

        {/* Heading */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-black leading-tight tracking-tighter mb-8 text-foreground drop-shadow-sm px-4">
          {current.heading}
        </h1>

        {/* Subtitle */}
        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-14 leading-relaxed font-medium px-4">
          {current.subtitle}
        </p>

        {/* Action Row */}
        <div className="flex flex-col items-center gap-12 sm:gap-16">
          <Button
            size="lg"
            onClick={handleAction}
            className="rounded-full h-16 px-12 text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-2xl shadow-primary/30 transition-all hover:scale-105"
          >
            {current.cta} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-16 p-8 md:p-12 rounded-[2.5rem] bg-card/50 border border-border/60 backdrop-blur-xl shadow-2xl max-w-4xl w-full mx-4"
          >
            {current.stats.map((stat, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="text-[10px] md:text-xs font-bold text-muted-foreground mb-2 uppercase tracking-widest flex items-center gap-2">
                  <span className="text-primary">{stat.icon}</span> {stat.label}
                </div>
                <div className="text-3xl md:text-4xl font-black text-foreground">{stat.value}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Image Showcase */}
      <motion.div
        className="mt-28 relative w-full max-w-6xl z-10 px-4"
        initial={{ opacity: 0, y: 120 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 1.4, ease: "easeOut" }}
      >
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-4/5 h-3/5 bg-primary/10 blur-[120px] z-[-1]" />

        <div className="relative p-1 bg-gradient-to-b from-white/10 to-transparent rounded-[2.5rem] overflow-hidden shadow-[0_60px_120px_-20px_rgba(0,0,0,0.8)] dark:shadow-[0_60px_120px_-20px_rgba(0,0,0,1)]">
          <img
            src={subjectPreview}
            alt="SnapED Dashboard"
            className="w-full h-auto rounded-[2.4rem] block transition-transform duration-700 hover:scale-[1.01]"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
